/**
 * skillNames.js — Feb 09 2026, Lane 2 (Subscriber Home restructure).
 *
 * Single source of truth for the short-form transferable-skill labels
 * used in-sentence (Skill in the Wild coverage note, Skill in the Wild
 * CTA, Skill Rings labels). The full canonical library name still
 * shows in card headlines and in the LLM's career story — this map
 * only trims the awkward "Innovation, Creativity & Entrepreneurship"
 * → "Innovation & Creativity" style compressions the founder-approved
 * spec calls for.
 *
 * Both the "&" library form (what Claude emits) and the "and" canonical
 * form (what internal coverage code uses) map to the same short label,
 * so the swap works no matter which spelling appears in a given string.
 *
 * `applyShortNames(text)` returns a new string with every longform hit
 * replaced by its short form. Longest keys are swapped first so a
 * substring like "Innovation & Creativity" inside the longer library
 * name never wins prematurely.
 */

// Full → short. Longforms deliberately duplicated for the two spellings.
export const SKILL_SHORT_NAMES = Object.freeze({
  // Library form (what Claude / the LLM emits)
  "Innovation, Creativity & Entrepreneurship": "Innovation & Creativity",
  "Critical Thinking & Problem Solving": "Critical Thinking",
  "Global Citizenship & Sustainability": "Global Citizenship",
  // Canonical form (what coverage.py and backend enums use)
  "Innovation, Creativity, and Entrepreneurship": "Innovation & Creativity",
  "Critical Thinking and Problem Solving": "Critical Thinking",
  "Global Citizenship and Sustainability": "Global Citizenship",
  // Unchanged — mapped to self so callers can safely round-trip anything.
  "Self-Directed Learning": "Self-Directed Learning",
  Collaboration: "Collaboration",
  Communication: "Communication",
  "Digital Literacy": "Digital Literacy",
});

/** Escape a literal string for use inside a RegExp. */
const _escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Return `text` with every longform in `SKILL_SHORT_NAMES` swapped for
 * its short form. Case-sensitive (LLM output is deterministic on this
 * axis). Preserves everything else in the string verbatim.
 *
 * Guards against `null` / `undefined` / non-string inputs by returning
 * them untouched — callers should not have to null-check first.
 */
export const applyShortNames = (text) => {
  if (typeof text !== "string" || !text) return text;
  const keys = Object.keys(SKILL_SHORT_NAMES).sort((a, b) => b.length - a.length);
  let out = text;
  for (const key of keys) {
    if (SKILL_SHORT_NAMES[key] === key) continue; // no-op mapping, skip regex work
    out = out.replace(new RegExp(_escapeRegExp(key), "g"), SKILL_SHORT_NAMES[key]);
  }
  return out;
};

/** Convenience — swap a single full label. Same guard as `applyShortNames`. */
export const toShortSkillName = (full) => {
  if (typeof full !== "string") return full;
  return SKILL_SHORT_NAMES[full] || full;
};
